import React, { useState } from 'react';
import {
  Mail,
  Cable,
  FileCode2,
  CheckCircle2,
  Zap,
} from 'lucide-react';

const CHANNELS = [
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    tagline: 'Works with every carrier — no setup on their side',
    description:
      'We send the offer or quote request to the carrier’s existing inbox. Our AI reads their reply — whether it’s a plain "yes / no", a counter-rate, or a follow-up question — extracts the numbers, and feeds the answer back into the auction in real time.',
    bullets: [
      'Zero onboarding required from the carrier',
      'AI handles free-form replies, attachments, and signatures',
      'Reply confidence + raw email kept for full audit',
    ],
    badge: 'Reaches 100% of your carrier base',
  },
  {
    id: 'api',
    name: 'API',
    icon: Cable,
    tagline: 'Sub-second offers and acceptances for hot freight',
    description:
      'When a carrier has API-level integration with Vibe, offers and acceptances are exchanged programmatically. Quotes come back in under a second — ideal for time-sensitive loads and high-volume lanes.',
    bullets: [
      'Real-time bidirectional exchange',
      'Structured responses — no parsing required',
      'Built-in retries, idempotency, and signed payloads',
    ],
    badge: 'Best for high-volume lanes',
  },
  {
    id: 'edi',
    name: 'EDI',
    icon: FileCode2,
    tagline: 'Standard transaction sets for enterprise carriers',
    description:
      'For carriers running on EDI, Vibe Engine speaks the standard freight transaction set out of the box. No middleware on your side, no extra mapping work for the carrier.',
    bullets: [
      '204 · Motor Carrier Load Tender',
      '990 · Response to Load Tender',
      '214 · Transportation Carrier Shipment Status',
    ],
    badge: 'Enterprise-ready',
  },
];

export default function CarrierChannels() {
  const [activeId, setActiveId] = useState('email');
  const active = CHANNELS.find((c) => c.id === activeId);

  return (
    <section id="channels" className="bg-white border-t border-ink-200">
      <div className="container-page py-20 lg:py-28">
        <div className="max-w-3xl">
          <p className="eyebrow">Carrier channels</p>
          <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
            Reach every carrier — the way they actually work.
          </h2>
          <p className="mt-5 text-lg text-ink-600 text-balance">
            Some carriers reply to emails. Some prefer API. Some live in EDI.
            Vibe Engine speaks all three, so your auction includes everyone —
            without forcing carriers onto a new portal.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {CHANNELS.map((c) => {
            const isActive = c.id === activeId;
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveId(c.id)}
                className={`text-left card-surface p-6 transition-all duration-200 ${
                  isActive
                    ? 'border-ink-900 shadow-cardHover -translate-y-0.5'
                    : 'hover:shadow-cardHover hover:-translate-y-0.5'
                }`}
                aria-pressed={isActive}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${
                      isActive
                        ? 'bg-ink-900 text-white'
                        : 'bg-ink-100 text-ink-900'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                      isActive
                        ? 'bg-ink-900 text-white'
                        : 'bg-ink-100 text-ink-600'
                    }`}
                  >
                    {c.id}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-ink-900">
                  {c.name}
                </h3>
                <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">
                  {c.tagline}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-ink-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {c.badge}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-6 card-surface p-7 sm:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-ink-900 text-white">
                <active.icon className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
                  Channel
                </div>
                <div className="text-base font-semibold text-ink-900">
                  {active.name}
                </div>
              </div>
            </div>

            <h3 className="mt-6 h-display text-2xl font-bold leading-tight text-balance">
              {active.tagline}
            </h3>
            <p className="mt-3 text-ink-600">{active.description}</p>

            <ul className="mt-6 space-y-3">
              {active.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-ink-700">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-ink-900 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            {active.id === 'email' && <EmailPreview />}
            {active.id === 'api' && <ApiPreview />}
            {active.id === 'edi' && <EdiPreview />}
          </div>
        </div>
      </div>
    </section>
  );
}

function EmailPreview() {
  return (
    <div className="card-surface p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-ink-900">
          Inbox · Pioneer Carriers
        </div>
        <span className="pill">
          <Mail className="h-3 w-3" /> AI parsed
        </span>
      </div>

      <div className="mt-4 rounded-xl border border-ink-200 bg-white">
        <div className="px-4 py-3 border-b border-ink-200">
          <div className="text-xs text-ink-500">From</div>
          <div className="text-sm text-ink-900">
            dispatch@pioneercarriers.com
          </div>
        </div>
        <div className="px-4 py-3 border-b border-ink-200">
          <div className="text-xs text-ink-500">Re</div>
          <div className="text-sm text-ink-900">
            Quote request — Dallas, TX → Atlanta, GA · Dry Van
          </div>
        </div>
        <div className="px-4 py-3 text-sm text-ink-700 leading-relaxed">
          <p>Hi team,</p>
          <p className="mt-2">
            We can cover this one. Best we can do is{' '}
            <span className="bg-yellow-100 rounded px-0.5">$2,295 all-in</span>,
            pickup tomorrow morning works.
          </p>
          <p className="mt-2">— Dispatch, Pioneer Carriers</p>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Extracted by Vibe AI
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <ExtractedField label="Decision" value="Accepted" />
          <ExtractedField label="Rate" value="$2,295" highlight />
          <ExtractedField label="Confidence" value="98%" />
        </div>
      </div>
    </div>
  );
}

function ApiPreview() {
  return (
    <div className="card-surface p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-ink-900">
          POST /v1/offers/respond
        </div>
        <span className="pill">
          <Zap className="h-3 w-3" /> 142 ms
        </span>
      </div>

      <pre className="mt-4 rounded-xl bg-ink-900 text-ink-100 text-[12px] leading-relaxed p-4 overflow-x-auto">
{`{
  "offer_id": "ofr_48219_3",
  "carrier_id": "crr_pioneer",
  "decision": "accepted",
  "rate": {
    "amount": 2295,
    "currency": "USD",
    "type": "all_in"
  },
  "service": {
    "pickup_at": "2026-05-15T08:00:00-05:00",
    "equipment": "dry_van"
  }
}`}
      </pre>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <ExtractedField label="Status" value="200 OK" />
        <ExtractedField label="Rate" value="$2,295" highlight />
        <ExtractedField label="Latency" value="142 ms" />
      </div>
    </div>
  );
}

function EdiPreview() {
  const TXNS = [
    { code: '204', name: 'Motor Carrier Load Tender', dir: 'OUT' },
    { code: '990', name: 'Response to Load Tender', dir: 'IN' },
    { code: '214', name: 'Carrier Shipment Status', dir: 'IN' },
    { code: '210', name: 'Freight Invoice', dir: 'IN' },
  ];
  return (
    <div className="card-surface p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-ink-900">
          EDI · Supported transactions
        </div>
        <span className="pill">
          <FileCode2 className="h-3 w-3" /> X12 standard
        </span>
      </div>

      <div className="mt-4 divide-y divide-ink-200 border border-ink-200 rounded-xl overflow-hidden">
        {TXNS.map((t) => (
          <div key={t.code} className="flex items-center justify-between px-4 py-3 bg-white">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-12 items-center justify-center rounded-md bg-ink-900 text-white text-xs font-semibold tabular-nums">
                {t.code}
              </span>
              <div className="text-sm text-ink-800">{t.name}</div>
            </div>
            <span
              className={`text-[10px] font-semibold tracking-wider px-2 py-0.5 rounded ${
                t.dir === 'OUT'
                  ? 'bg-ink-100 text-ink-700'
                  : 'bg-emerald-50 text-emerald-700'
              }`}
            >
              {t.dir}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-ink-500">
        Additional X12 sets available on request. Vibe Engine handles
        translation, envelopes, and acknowledgements (997).
      </p>
    </div>
  );
}

function ExtractedField({ label, value, highlight }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        highlight
          ? 'bg-ink-900 border-ink-900 text-white'
          : 'bg-white border-ink-200'
      }`}
    >
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${
        highlight ? 'text-white/60' : 'text-ink-500'
      }`}>
        {label}
      </div>
      <div className={`mt-0.5 text-sm font-semibold tabular-nums ${
        highlight ? 'text-white' : 'text-ink-900'
      }`}>
        {value}
      </div>
    </div>
  );
}
